<template>
  <div class="code-block">
    <div class="code-block__header">
      <span class="code-block__lang">{{ displayLanguage }}</span>
      <button
        type="button"
        class="code-block__copy"
        :aria-label="'Copy ' + displayLanguage + ' snippet'"
        v-clipboard:success="onCopied"
        v-clipboard="() => code"
      >
        <v-icon size="16" color="white">{{
          copied ? "mdi-check" : "mdi-content-copy"
        }}</v-icon>
        <span>{{ copied ? "Copied" : "Copy" }}</span>
      </button>
    </div>
    <div class="code-block__body">
      <prism :language="language" :code="code"></prism>
    </div>
  </div>
</template>

<script>
import Prism from "vue-prismjs";

const LANG_LABELS = {
  bash: "Bash",
  shell: "Shell",
  curl: "cURL",
  javascript: "JavaScript",
  js: "JavaScript",
  typescript: "TypeScript",
  python: "Python",
  php: "PHP",
  java: "Java",
  csharp: "C#",
  json: "JSON",
  yaml: "YAML",
  html: "HTML",
  markup: "HTML",
};

export default {
  name: "CodeBlock",
  components: { Prism },
  props: {
    code: { type: String, required: true },
    language: { type: String, default: "bash" },
    label: { type: String, default: "" },
  },
  data() {
    return { copied: false };
  },
  computed: {
    displayLanguage() {
      if (this.label) return this.label;
      return LANG_LABELS[this.language] || this.language;
    },
  },
  methods: {
    onCopied() {
      this.copied = true;
      this.$notify({
        group: "main",
        text: "Copied to clipboard",
        duration: 1200,
      });
      setTimeout(() => {
        this.copied = false;
      }, 1600);
    },
  },
};
</script>

<style scoped lang="scss">
.code-block {
  background-color: #0f172a;
  border-radius: 12px;
  overflow: hidden;
  border: 1px solid rgba(15, 23, 42, 0.2);

  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 10px 14px;
    background-color: rgba(255, 255, 255, 0.04);
    border-bottom: 1px solid rgba(255, 255, 255, 0.06);
  }

  &__lang {
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: rgba(255, 255, 255, 0.55);
    font-family: "SF Mono", "Menlo", "Monaco", "Consolas", monospace;
  }

  &__copy {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 4px 10px;
    background: rgba(255, 255, 255, 0.06);
    border: 1px solid rgba(255, 255, 255, 0.08);
    border-radius: 6px;
    color: #ffffff;
    font-size: 12px;
    font-weight: 500;
    cursor: pointer;
    transition: background-color 120ms ease;

    &:hover {
      background: rgba(255, 255, 255, 0.12);
    }

    &:focus-visible {
      outline: 2px solid #e2782f;
      outline-offset: 2px;
    }
  }

  &__body {
    max-height: 480px;
    overflow: auto;

    ::v-deep pre,
    ::v-deep code {
      background: transparent !important;
      font-family: "SF Mono", "Menlo", "Monaco", "Consolas",
        "Liberation Mono", monospace !important;
      text-shadow: none !important;
      color: #e2e8f0;
    }

    ::v-deep pre {
      margin: 0;
      padding: 16px 18px;
      font-size: 13px;
      line-height: 1.6;
      border-radius: 0;
      box-shadow: none;
      max-height: none;
      tab-size: 2;
      -webkit-font-smoothing: antialiased;
      -moz-osx-font-smoothing: grayscale;
      white-space: pre;
      word-break: normal;
      hyphens: none;
    }

    /* Reset any background bleed from globally-imported prism themes */
    ::v-deep .token {
      background: transparent !important;
    }

    /* Prism token colors tuned for dark navy (#0F172A) background */
    ::v-deep .token.comment,
    ::v-deep .token.prolog,
    ::v-deep .token.doctype,
    ::v-deep .token.cdata {
      color: #64748b;
      font-style: italic;
    }

    ::v-deep .token.punctuation {
      color: #94a3b8;
    }

    ::v-deep .token.namespace {
      opacity: 0.75;
    }

    ::v-deep .token.property,
    ::v-deep .token.tag,
    ::v-deep .token.boolean,
    ::v-deep .token.number,
    ::v-deep .token.constant,
    ::v-deep .token.symbol,
    ::v-deep .token.deleted {
      color: #fb923c;
    }

    ::v-deep .token.selector,
    ::v-deep .token.attr-name,
    ::v-deep .token.string,
    ::v-deep .token.char,
    ::v-deep .token.builtin,
    ::v-deep .token.inserted {
      color: #fcd34d;
    }

    ::v-deep .token.operator,
    ::v-deep .token.entity,
    ::v-deep .token.url,
    ::v-deep .token.variable {
      color: #67e8f9;
    }

    ::v-deep .token.atrule,
    ::v-deep .token.attr-value,
    ::v-deep .token.keyword {
      color: #c4b5fd;
    }

    ::v-deep .token.function,
    ::v-deep .token.class-name {
      color: #f9a8d4;
    }

    ::v-deep .token.regex,
    ::v-deep .token.important {
      color: #fb923c;
    }

    ::v-deep .token.important,
    ::v-deep .token.bold {
      font-weight: 700;
    }

    ::v-deep .token.italic {
      font-style: italic;
    }
  }
}
</style>
